import {
  FiCheckCircle,
  FiClock,
  FiEdit,
  FiEye,
  FiTrash2,
  FiUploadCloud,
} from "react-icons/fi";

const STATUS_STYLES = {
  "In Transit":
    "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300",
  "Arrived at Port":
    "bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-300",
  Delivered:
    "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
  "Departed from Origin Port":
    "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
};

function StatusBadge({ status }) {
  const label = status || "In Transit";
  const style = STATUS_STYLES[label] || STATUS_STYLES["In Transit"];

  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-full px-3 py-1.5 text-[11px] font-semibold leading-none ${style}`}
    >
      {label}
    </span>
  );
}

function ClearanceBadge({ cleared }) {
  return cleared ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
      <FiCheckCircle size={12} />
      Cleared
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-500 dark:bg-slate-800 dark:text-slate-300">
      <FiClock size={12} />
      Pending
    </span>
  );
}

function formatDate(date) {
  if (!date) return "N/A";

  const parsed = new Date(date);

  if (Number.isNaN(parsed.getTime())) return "N/A";

  return parsed.toLocaleDateString("en-GB");
}

function DetailRow({ label, value, valueClassName = "" }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="shrink-0 text-sm text-slate-500 dark:text-slate-400">
        {label}
      </span>

      <span
        className={`min-w-0 break-words text-right text-sm font-semibold text-slate-800 dark:text-slate-100 ${valueClassName}`}
      >
        {value || "N/A"}
      </span>
    </div>
  );
}

function ActionButton({
  icon: Icon,
  label,
  onClick,
  className,
  ariaLabel,
}) {
  if (typeof onClick !== "function") return null;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex min-w-[108px] flex-1 items-center justify-center gap-2 rounded-xl border px-3 py-2.5 text-xs font-semibold transition focus:outline-none focus:ring-4 ${className}`}
      aria-label={ariaLabel || label}
      title={ariaLabel || label}
    >
      <Icon size={15} />
      {label}
    </button>
  );
}

export default function ContainerCard({
  container,
  customerName,
  onView,
  onUpload,
  onEdit,
  onDelete,
}) {
  const hasActions =
    typeof onView === "function" ||
    typeof onUpload === "function" ||
    typeof onEdit === "function" ||
    typeof onDelete === "function";

  return (
    <article className="overflow-hidden rounded-2xl border border-slate-200 bg-white text-slate-900 shadow-sm transition duration-200 hover:-translate-y-0.5 hover:shadow-lg dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100 dark:shadow-none">
      <div className="border-b border-slate-100 bg-slate-50/70 px-5 py-4 dark:border-slate-800 dark:bg-slate-950/60">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h3 className="truncate text-base font-bold text-slate-950 dark:text-white">
              {container?.bookingBLNo || "Booking/BL not available"}
            </h3>

            <p className="mt-1 text-xs font-medium text-slate-500 dark:text-slate-400">
              S.No: {container?.serialNo || "N/A"}
            </p>
          </div>

          <StatusBadge status={container?.status} />
        </div>
      </div>

      <div className="space-y-3 px-5 py-4">
        <DetailRow
          label="Container"
          value={container?.containerNumber || "N/A"}
        />

        <DetailRow label="Date" value={formatDate(container?.date)} />

        <DetailRow
          label="Vessel"
          value={container?.vesselName || "N/A"}
        />

        <DetailRow
          label="Origin"
          value={container?.origin || "N/A"}
        />

        <DetailRow
          label="Destination"
          value={container?.destination || "N/A"}
        />

        <DetailRow
          label="ETA"
          value={formatDate(container?.expectedArrival)}
        />

        <div className="flex items-center justify-between gap-4">
          <span className="text-sm text-slate-500 dark:text-slate-400">
            Customs
          </span>

          <ClearanceBadge cleared={container?.customCleared} />
        </div>

        <DetailRow
          label="Customer"
          value={
            customerName ||
            (container?.customerId
              ? `Customer #${container.customerId}`
              : "Not assigned")
          }
        />
      </div>

      {hasActions ? (
        <div className="flex flex-wrap gap-2 border-t border-slate-100 bg-slate-50/50 px-4 py-3 dark:border-slate-800 dark:bg-slate-950/40">
          <ActionButton
            icon={FiEye}
            label="View"
            onClick={
              typeof onView === "function"
                ? () => onView(container)
                : undefined
            }
            ariaLabel="View container details"
            className="border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-100 focus:ring-slate-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:border-slate-600 dark:hover:bg-slate-800 dark:focus:ring-slate-800"
          />

          <ActionButton
            icon={FiUploadCloud}
            label="Documents"
            onClick={
              typeof onUpload === "function"
                ? () => onUpload(container)
                : undefined
            }
            ariaLabel="Upload container documents"
            className="border-violet-200 bg-violet-50 text-violet-700 hover:border-violet-300 hover:bg-violet-100 focus:ring-violet-100 dark:border-violet-900 dark:bg-violet-950/60 dark:text-violet-300 dark:hover:border-violet-800 dark:hover:bg-violet-950 dark:focus:ring-violet-950"
          />

          <ActionButton
            icon={FiEdit}
            label="Edit"
            onClick={
              typeof onEdit === "function"
                ? () => onEdit(container)
                : undefined
            }
            ariaLabel="Edit container"
            className="border-blue-200 bg-blue-50 text-blue-700 hover:border-blue-300 hover:bg-blue-100 focus:ring-blue-100 dark:border-blue-900 dark:bg-blue-950/60 dark:text-blue-300 dark:hover:border-blue-800 dark:hover:bg-blue-950 dark:focus:ring-blue-950"
          />

          <ActionButton
            icon={FiTrash2}
            label="Delete"
            onClick={
              typeof onDelete === "function"
                ? () => onDelete(container)
                : undefined
            }
            ariaLabel="Delete container"
            className="border-red-200 bg-red-50 text-red-700 hover:border-red-300 hover:bg-red-100 focus:ring-red-100 dark:border-red-900 dark:bg-red-950/60 dark:text-red-300 dark:hover:border-red-800 dark:hover:bg-red-950 dark:focus:ring-red-950"
          />
        </div>
      ) : null}
    </article>
  );
}